// EIP-712 typed-data hashing. We implement it inline (rather than pulling in
// viem / ethers) so the iframe bundle stays small and predictable. The
// algorithm follows https://eips.ethereum.org/EIPS/eip-712 verbatim.
//
// Usage:
//   const digest = hashTypedData(typedData);
//   const sig    = signDigest(privateKey, digest);
//
// Anything that lives outside this module talks to the iframe in JSON; this
// module is the only place that has to know about ABI encoding.

import { keccak_256 } from '@noble/hashes/sha3';
import { hexToBytes } from './wallet';

export type TypedDataField = { name: string; type: string };

export type TypedData = {
  domain: Record<string, unknown>;
  types: Record<string, TypedDataField[]>;
  primaryType: string;
  message: Record<string, unknown>;
};

// hashTypedData returns the 32-byte digest the user actually signs.
//   keccak256("\x19\x01" || domainSeparator || hashStruct(message))
export function hashTypedData(td: TypedData): Uint8Array {
  const types = withDomainType(td);
  const domainSeparator = hashStruct('EIP712Domain', td.domain, types);
  const messageHash = hashStruct(td.primaryType, td.message, types);

  const out = new Uint8Array(2 + 32 + 32);
  out[0] = 0x19;
  out[1] = 0x01;
  out.set(domainSeparator, 2);
  out.set(messageHash, 34);
  return keccak_256(out);
}

// ---- internals ----

// withDomainType ensures the EIP712Domain entry is present + ordered (some
// callers omit it because the domain shape is implied).
function withDomainType(td: TypedData): Record<string, TypedDataField[]> {
  if (td.types.EIP712Domain) return td.types;
  // Derive from the keys actually present in `domain`. Order matters.
  const order: TypedDataField[] = [];
  if ('name' in td.domain) order.push({ name: 'name', type: 'string' });
  if ('version' in td.domain) order.push({ name: 'version', type: 'string' });
  if ('chainId' in td.domain) order.push({ name: 'chainId', type: 'uint256' });
  if ('verifyingContract' in td.domain) order.push({ name: 'verifyingContract', type: 'address' });
  if ('salt' in td.domain) order.push({ name: 'salt', type: 'bytes32' });
  return { ...td.types, EIP712Domain: order };
}

function hashStruct(primary: string, data: Record<string, unknown>, types: Record<string, TypedDataField[]>): Uint8Array {
  const typeHash = keccak_256(new TextEncoder().encode(encodeType(primary, types)));
  const fields = types[primary];
  if (!fields) throw new Error(`unknown type ${primary}`);
  const enc = new Uint8Array(32 * (1 + fields.length));
  enc.set(typeHash, 0);
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    enc.set(encodeValue(f.type, data[f.name], types), 32 * (i + 1));
  }
  return keccak_256(enc);
}

// encodeType builds the canonical type signature, including referenced
// structs in alphabetical order, e.g.
//   "Mail(Person from,Person to,string contents)Person(string name,address wallet)"
function encodeType(primary: string, types: Record<string, TypedDataField[]>): string {
  const deps = collectDeps(primary, types, new Set()).filter((t) => t !== primary).sort();
  const ordered = [primary, ...deps];
  return ordered.map((t) => `${t}(${types[t].map((f) => `${f.type} ${f.name}`).join(',')})`).join('');
}

function collectDeps(t: string, types: Record<string, TypedDataField[]>, seen: Set<string>): string[] {
  if (seen.has(t)) return [...seen];
  if (!types[t]) return [...seen];
  seen.add(t);
  for (const f of types[t]) {
    const base = stripArray(f.type);
    if (types[base]) collectDeps(base, types, seen);
  }
  return [...seen];
}

function stripArray(t: string): string {
  const i = t.indexOf('[');
  return i === -1 ? t : t.slice(0, i);
}

// encodeValue returns the 32-byte ABI encoding of one field.
function encodeValue(type: string, value: unknown, types: Record<string, TypedDataField[]>): Uint8Array {
  if (type.endsWith(']')) {
    // Dynamic / fixed array: keccak256(concat(encodeValue(elem))).
    const inner = type.slice(0, type.lastIndexOf('['));
    const arr = value as unknown[];
    const buf = new Uint8Array(32 * arr.length);
    for (let i = 0; i < arr.length; i++) buf.set(encodeValue(inner, arr[i], types), 32 * i);
    return keccak_256(buf);
  }
  if (type === 'string') {
    return keccak_256(new TextEncoder().encode(String(value)));
  }
  if (type === 'bytes') {
    const b = typeof value === 'string' ? hexToBytes(value) : (value as Uint8Array);
    return keccak_256(b);
  }
  if (type.startsWith('bytes')) {
    // Fixed-size: bytes1..bytes32, right-padded.
    const b = typeof value === 'string' ? hexToBytes(value) : (value as Uint8Array);
    const out = new Uint8Array(32);
    out.set(b, 0);
    return out;
  }
  if (type === 'address') {
    const b = hexToBytes(String(value));
    const out = new Uint8Array(32);
    out.set(b, 12);
    return out;
  }
  if (type === 'bool') {
    const out = new Uint8Array(32);
    out[31] = value ? 1 : 0;
    return out;
  }
  if (type.startsWith('uint') || type.startsWith('int')) {
    return encodeInt(value);
  }
  // Nested struct.
  if (types[type]) {
    return hashStruct(type, value as Record<string, unknown>, types);
  }
  throw new Error(`unsupported EIP-712 type: ${type}`);
}

// encodeInt big-endian into 32 bytes. Accepts number | bigint | string |
// hex-string (`0x…`). Negative numbers are encoded two's complement (rare in
// EIP-712 payloads but handled for completeness).
function encodeInt(value: unknown): Uint8Array {
  let bi: bigint;
  if (typeof value === 'bigint') {
    bi = value;
  } else if (typeof value === 'number') {
    bi = BigInt(value);
  } else if (typeof value === 'string') {
    bi = value.startsWith('0x') ? BigInt(value) : BigInt(value);
  } else {
    throw new Error('invalid int value');
  }
  const out = new Uint8Array(32);
  if (bi < 0n) {
    bi = (1n << 256n) + bi; // two's complement in 256 bits
  }
  for (let i = 31; i >= 0; i--) {
    out[i] = Number(bi & 0xffn);
    bi >>= 8n;
  }
  return out;
}
